/** Sensei paste-import: generic id/name only. Do not invent a Sensei schema. */

export type SenseiExportKind = "customer" | "inventory";
export type SenseiExportSalePath = "inventory_no_sale_yet" | "cash" | "finance" | "rto";

export type SenseiExportTranslation = {
  external_source_id: string;
  kind: SenseiExportKind;
  proposed_sale_path: SenseiExportSalePath | null;
  proposed_options: Record<string, unknown>;
  customer_display_name: string;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
  return null;
}

function unwrap(payload: unknown): Record<string, unknown> | null {
  const root = asRecord(payload);
  if (!root) return null;
  const nested = asRecord(root.payload) ?? asRecord(root.data) ?? asRecord(root.order);
  return nested ? { ...nested, ...root } : root;
}

function str(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return "";
}

function firstId(root: Record<string, unknown>): string {
  const keys = ["id", "order_id", "orderId", "order_number", "orderNumber", "job_id", "jobId", "quote_id", "quoteId"];
  for (const key of keys) {
    const direct = str(root[key]);
    if (direct) return direct;
    const nested = asRecord(root[key]);
    if (nested) {
      const id = str(nested.id) || str(nested.order_id) || str(nested.orderNumber);
      if (id) return id;
    }
  }
  return "";
}

function customerName(root: Record<string, unknown>): string {
  const customer = asRecord(root.customer) ?? asRecord(root.buyer) ?? asRecord(root.contact);
  const fromObj = customer
    ? str(customer.name) ||
      [str(customer.first_name) || str(customer.firstName), str(customer.last_name) || str(customer.lastName)]
        .filter(Boolean)
        .join(" ")
    : "";
  return fromObj || str(root.customer_name) || str(root.customerName);
}

export function parseSenseiExportJson(text: string): { ok: true; payload: unknown } | { ok: false; error: string } {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, error: "Paste the Sensei JSON export." };
  try {
    return { ok: true, payload: JSON.parse(trimmed) as unknown };
  } catch {
    return { ok: false, error: "Sensei body must be JSON." };
  }
}

export function translateSenseiExport(
  payload: unknown
): { ok: true; translation: SenseiExportTranslation } | { ok: false; error: string } {
  const root = unwrap(payload);
  if (!root) return { ok: false, error: "Sensei export must be a JSON object." };

  const external_source_id = firstId(root);
  if (!external_source_id) {
    return {
      ok: false,
      error: "Could not find an id (id, order_id, orderNumber, job_id, or quote_id). Do not invent Sensei fields.",
    };
  }

  const rawKind = (str(root.kind) || str(root.order_type) || str(root.orderType)).toLowerCase();
  const kind: SenseiExportKind =
    rawKind.includes("inventory") || rawKind.includes("stock") ? "inventory" : "customer";
  const name = customerName(root);
  const proposed_options: Record<string, unknown> = {};
  if (name) proposed_options.customer_name = name;

  const pay = (str(root.sale_path) || str(root.salePath) || str(root.payment_type) || str(root.paymentType)).toLowerCase();
  let proposed_sale_path: SenseiExportSalePath | null = kind === "inventory" ? "inventory_no_sale_yet" : null;
  if (kind === "customer") {
    if (pay.includes("rto") || pay.includes("rent")) proposed_sale_path = "rto";
    else if (pay.includes("finance") || pay.includes("loan")) proposed_sale_path = "finance";
    else if (pay.includes("cash")) proposed_sale_path = "cash";
  }

  return {
    ok: true,
    translation: {
      external_source_id,
      kind,
      proposed_sale_path,
      proposed_options,
      customer_display_name: name,
    },
  };
}

export function senseiRawPayload(payload: unknown): Record<string, unknown> {
  return asRecord(payload) ?? { value: payload };
}
